// dropPartition — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-partition', table, partitionName, … })` op
// (DSE2 §3.E1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1 — Partitioning UI.
//
// Per-engine surface (mirrors DSE2's `synthesizeDropPartition`):
//   - pgsql:  ALTER TABLE t DETACH PARTITION p [CONCURRENTLY] — detaches
//             without deleting data; the child table remains.
//   - mysql:  ALTER TABLE t DROP PARTITION p — drops the partition AND data.
//   - oracle: ALTER TABLE t DROP PARTITION p [UPDATE GLOBAL INDEXES].
//   - mssql:  rejects — mssql drops partitions via ALTER PARTITION FUNCTION
//             pf MERGE RANGE, out of v2 Slice E1 scope.
//   - sqlite: rejects — no partitioning support.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { QualifiedName } from '../../../../sql-grammar/model';
import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface DropPartitionArgs {
  readonly table: QualifiedName;
  readonly partitionName: string;
  /** pgsql-only: emit `DETACH PARTITION p CONCURRENTLY`. */
  readonly pgsqlConcurrently?: boolean;
  /** oracle-only: emit `… UPDATE GLOBAL INDEXES`. */
  readonly oracleUpdateGlobalIndexes?: boolean;
}

export function buildDropPartitionEdit(args: DropPartitionArgs): DdlEdit {
  return {
    op: 'drop-partition',
    table: args.table,
    partitionName: args.partitionName,
    pgsqlConcurrently: args.pgsqlConcurrently,
    oracleUpdateGlobalIndexes: args.oracleUpdateGlobalIndexes,
  };
}

export async function commitDropPartition(
  ctx: VqeDdlEditContext,
  args: DropPartitionArgs,
): Promise<void> {
  const edit = buildDropPartitionEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const DROP_PARTITION_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'postgres', 'mysql', 'oracle',
] as const);

export function isDropPartitionSupported(engine: SqlEngine): boolean {
  return DROP_PARTITION_SUPPORTED.has(engine);
}

export function getDropPartitionUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'SQL Server drops partitions via ALTER PARTITION FUNCTION pf MERGE RANGE '
      + 'on the bound function. Direct partition add/drop on the table is not '
      + 'supported.'
    );
  }
  if (engine === 'sqlite') {
    return 'SQLite has no partitioning support.';
  }
  return '';
}

export function isDropPartitionConcurrentlySupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isDropPartitionUpdateGlobalIndexesSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

/** Pre-flight validator. Returns null when args are acceptable, else a
 *  user-facing message. */
export function validateDropPartitionForEngine(
  engine: SqlEngine,
  args: DropPartitionArgs,
): string | null {
  if (!isDropPartitionSupported(engine)) {
    return getDropPartitionUnsupportedTooltip(engine);
  }
  if (args.partitionName.trim().length === 0) {
    return 'Partition name is required.';
  }
  if (args.pgsqlConcurrently && engine !== 'postgres') {
    return 'CONCURRENTLY is supported only on PostgreSQL DETACH PARTITION.';
  }
  if (args.oracleUpdateGlobalIndexes && engine !== 'oracle') {
    return 'UPDATE GLOBAL INDEXES is supported only on Oracle DROP PARTITION.';
  }
  return null;
}
